import { action, computed, observable, makeObservable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import Tasks from 'APP/Tasks';
import { PayloadType } from 'APP/model/message/messageModel.types';
import Entities from 'STORE';
import { Forward } from 'STORE/Messages/Message/Forward/Forward';
import { Quote } from 'STORE/Messages/Message/Quote/Quote';

import { AdvertisementPayload } from './Payload/AdvertisementPayload/AdvertisementPayload';
import { AlbumPayload } from './Payload/AlbumPayload/AlbumPayload';
import { ArticlePayload } from './Payload/ArticlePayload/ArticlePayload';
import { AudioPayload } from './Payload/AudioPayload/AudioPayload';
import { ButtonsPayload } from './Payload/ButtonsPayload/ButtonsPayload';
import { ButtonsSelectedPayload } from './Payload/ButtonsSelectedPayload/ButtonsSelectedPayload';
import { CallPayload } from './Payload/CallPayload/CallPayload';
import { ContactPayload } from './Payload/ContactPayload/ContactPayload';
import { DeletedByMePayload } from './Payload/DeletedByMePayload/DeletedByMePayload';
import { E2eePayload } from './Payload/E2eePayload/E2eePayload';
import { FilePayload } from './Payload/FilePayload/FilePayload';
import { ImagePayload } from './Payload/ImagePayload/ImagePayload';
import { LocationPayload } from './Payload/LocationPayload/LocationPayload';
import { Payload } from './Payload/Payload';
import { StickerPayload } from './Payload/StickerPayload/StickerPayload';
import { StreamPayload } from './Payload/StreamPayload/StreamPayload';
import { SystemPayload } from './Payload/SystemPayload/SystemPayload';
import { TextPayload } from './Payload/TextPayload/TextPayload';
import { VideoPayload } from './Payload/VideoPayload/VideoPayload';
import { VoicePayload } from './Payload/VoicePayload/VoicePayload';

export const payloadClasses = {
  [PayloadType.RichText]: TextPayload,
  [PayloadType.E2ee]: E2eePayload,
  [PayloadType.File]: FilePayload,
  [PayloadType.Image]: ImagePayload,
  [PayloadType.Album]: AlbumPayload,
  [PayloadType.Video]: VideoPayload,
  [PayloadType.Contact]: ContactPayload,
  [PayloadType.Location]: LocationPayload,
  [PayloadType.Sticker]: StickerPayload,
  [PayloadType.VoiceMessage]: VoicePayload,
  [PayloadType.AudioMessage]: AudioPayload,
  [PayloadType.Article]: ArticlePayload,
  [PayloadType.Advertisement]: AdvertisementPayload,

  // message deleted only for me
  [PayloadType.MessageWasDeletedByMe]: DeletedByMePayload,
  // bots
  [PayloadType.Buttons]: ButtonsPayload,
  [PayloadType.ButtonsSelected]: ButtonsSelectedPayload,
  // call system message
  [PayloadType.Call]: CallPayload,
  [PayloadType.SystemCall]: CallPayload,
  [PayloadType.SystemCallStarted]: CallPayload,
  [PayloadType.SystemCallFinished]: CallPayload,
  //stream
  [PayloadType.SystemStream]: StreamPayload,
  // system
  [PayloadType.SystemChatRenamed]: SystemPayload,
  [PayloadType.SystemChatDescriptionChanged]: SystemPayload,
  [PayloadType.SystemChatTypeChanged]: SystemPayload,
  [PayloadType.SystemChatCategoryChanged]: SystemPayload,
  [PayloadType.SystemChatAvatarChanged]: SystemPayload,
  [PayloadType.SystemChatOwnerChanged]: SystemPayload,
  [PayloadType.SystemChatAdminsAdded]: SystemPayload,
  [PayloadType.SystemChatAdminsRemoved]: SystemPayload,
  [PayloadType.SystemChatUsersRemoved]: SystemPayload,
  [PayloadType.SystemChatUsersBanned]: SystemPayload,
  [PayloadType.SystemChatJoined]: SystemPayload,
  [PayloadType.SystemChatAdded]: SystemPayload,
  [PayloadType.SystemChatLeft]: SystemPayload,
  [PayloadType.SystemChatMessagePinned]: SystemPayload,
  [PayloadType.SystemChatNewlyCreatedWelcome]: SystemPayload,
  [PayloadType.SystemThreadNewlyCreatedWelcome]: SystemPayload,
  [PayloadType.SystemChatNewlyCreatedPhoneBookUserJoined]: SystemPayload,
  [PayloadType.SystemChatMessagesSettingsChanged]: SystemPayload,
  [PayloadType.SystemChatAddedToSpace]: SystemPayload,
  [PayloadType.SystemChatRemovedFromSpace]: SystemPayload,
  [PayloadType.SystemChatTtlChanged]: SystemPayload,
};

export class Message {
  // TODO: split chat message | system message | virtual(aka new message) | last message into sepparate classes
  payload;
  id;
  groupId;
  senderId;
  clientTime;
  serverTime;
  editTime;
  repliedMessage;
  quote;
  forwardMessage;
  deepLink;
  type;
  clientUuid;
  usersReactions;

  @observable views;
  @observable expectedServerTime = 0;
  @observable optimisticReactions = null;
  @observable isLoadingTranslate = false;
  @observable isShowTranslate = false;
  @observable threadId = null;

  constructor(data) {
    makeObservable(this);

    const {
      id,
      clientUuid,
      groupId,
      senderId,
      clientTime,
      serverTime,
      expectedServerTime,
      editTime,
      repliedMessage,
      quote,
      deepLink,
      forwardedMessage,
      payload,
      usersReactions,
      views,
      threadId,
      links,
    } = data || {};

    const now = Date.now();
    const payloadType = (payload && payload.payloadType) || null;
    const PayloadClass = payloadClasses[payloadType];

    this.clientUuid = clientUuid || uuidv4();
    this.id = id?.toString() || this.clientUuid;
    this.groupId = groupId?.toString() || null;
    this.senderId = senderId?.toString() || null;
    this.clientTime = parseInt(clientTime, 10) || now; // clientTime
    this.serverTime = parseInt(serverTime, 10) || expectedServerTime; // serverTime
    this.expectedServerTime = this.serverTime;
    this.editTime = parseInt(editTime, 10) || null; // editTime
    this.deepLink = deepLink || null;
    this.threadId = threadId || null;
    this.links = links || [];

    this.repliedMessage = repliedMessage ? new Message(repliedMessage) : null;
    this.quote = quote ? new Quote({ source: quote.source, text: quote.text }) : null;
    this.forward = forwardedMessage ? new Forward({ ...forwardedMessage, message: this }) : null;
    this.payload = PayloadClass
      ? new PayloadClass({ ...payload, message: this })
      : new Payload({ ...payload, payloadType: 'Unknown', message: this });
    this.usersReactions = usersReactions;
    this.views = views;
  }

  toJSON() {
    const { groupId, clientTime, clientUuid, repliedMessage, quote, payload } = this;

    return {
      groupId,
      clientTime,
      clientUuid,
      quotedMessageData: repliedMessage
        ? {
            sourceChatId: repliedMessage.groupId || '',
            sourceMessageId: repliedMessage.id || '',
          }
        : quote?.toJSON(),
      payload: payload.toJSON(),
    };
  }

  @computed
  get displayTime() {
    return this.isFake ? this.clientTime : this.serverTime;
  }

  @computed
  get unread() {
    return !this.isFake && this.serverTime > this.group.seenTs;
  }

  @computed
  get hasTranslation() {
    return Boolean(this.isLoadingTranslate || this.translatedPayload);
  }

  @action
  setMessageTime(expectedServerTime, serverTime) {
    this.expectedServerTime = expectedServerTime;
    if (!this.serverTime) {
      this.serverTime = serverTime;
    }
  }

  @computed
  get group() {
    return Entities.GroupsStore.getGroupById(this.groupId);
  }

  @computed
  get isP2PGroup() {
    return this.group && this.group.isP2P;
  }

  @computed
  get isBot() {
    return this.group && this.group.isBot;
  }

  @computed
  get isChatGroup() {
    return this.group && this.group.isChatGroup;
  }

  @computed
  get isPublicGroup() {
    return this.group && this.group.isChannel;
  }

  @computed
  get isSavedMessages() {
    return this.group && this.group.isSavedMessages;
  }

  @computed
  get isForwardToSavedMessages() {
    return Boolean(this.isSavedMessages && this.forward);
  }

  @computed
  get isSavedMessagesLeft() {
    return this.isForwardToSavedMessages;
  }

  @computed
  get isFake() {
    return this.clientUuid === this.id;
  }

  @computed
  get isFakeGroup() {
    return this.group && this.group.isFake;
  }

  get isSecretGroup() {
    return this.group && this.group.isSecretGroup;
  }

  @computed
  get isScheduled() {
    return this.group && this.group.isFake;
  }

  @computed
  get hasStatus() {
    return this.senderId === Entities.usersStore.Me.id && !this.payload.isSystem;
  }

  @computed
  get isFailed() {
    return this.hasStatus && this.hasError;
  }

  @computed
  get isSeen() {
    return (
      this.hasStatus &&
      !this.isFake &&
      this.isP2PGroup &&
      this.group.opponentSeenTs >= this.serverTime
    );
  }

  @computed
  get isSending() {
    return this.hasStatus && this.isFake && !this.hasError;
  }

  @computed
  get isSent() {
    return this.hasStatus && !this.isFake;
  }

  @computed
  get mapKey() {
    if (this.fromMe) {
      // so that fake media messages do not blink after sending
      return this.clientUuid + '-' + this.expectedServerTime;
    }

    return this.id;
  }

  get senderUser() {
    if (this.isForwardToSavedMessages) {
      return this.forward.senderUser;
    }

    return Entities.usersStore.getUserById(this.senderId);
  }

  @computed
  get fromMe() {
    if (this.isForwardToSavedMessages) {
      return false;
    }

    return this.senderId === Entities.usersStore.Me.id;
  }

  @computed
  get isLeftPosition() {
    if (this.group.isChannel || this.group.isServiceMessages) {
      return true;
    }

    return !this.fromMe;
  }

  @computed
  get isSenderNameShow() {
    if (this.group.isServiceMessages) {
      return false;
    }

    if (this.isForwardToSavedMessages) {
      return true;
    }

    if (this.group.isChannel && this.group.withMeInAdmins && this.fromMe) {
      return false;
    }

    if (this.group.isChannel && !this.fromMe && !this.group.withMeInAdmins) {
      return false;
    }

    if (this.group.isChannel && !this.fromMe) {
      return true;
    }

    return (
      !this.isPublicGroup &&
      !this.isP2PGroup &&
      !this.isBot &&
      this.isLocalFirst &&
      this.senderUser &&
      this.isLeftPosition
    );
  }

  @computed
  get isAvatarShow() {
    if (this.isP2PGroup) {
      return false;
    }

    if (this.group.isServiceMessages) {
      return false;
    }

    if (this.isForwardToSavedMessages) {
      return true;
    }

    return !this.isPublicGroup && this.isLocalFirst && this.senderUser;
  }

  @computed
  get avatarUrl() {
    if (this.forward) {
      if (this.isForwardToSavedMessages) {
        if (this.forward.isFromChannel) {
          return this.forward.groupFrom?.avatarUrl;
        }

        return Entities.usersStore.getUserById(this.forward.senderId)?.avatarUrl || null;
      }
    }

    if (this.isPublicGroup) {
      return this.group && this.group.avatarUrl;
    }

    return this.senderUser.avatarUrl;
  }

  @computed
  get avatarTitle() {
    if (this.forward) {
      if (this.isForwardToSavedMessages) {
        if (this.forward.isFromChannel) {
          return this.forward.avatarTitle;
        }

        return Entities.usersStore.getUserById(this.forward.senderId)?.avatarTitle || null;
      }
    }

    if (this.group.isChannel && this.group.withMeInAdmins) {
      return this.senderUser?.avatarTitle || '';
    }

    if (this.group.isChannel && !this.group.withMeInAdmins && !this.fromMe) {
      return this.group && this.group.avatarTitle;
    }

    if (this.isPublicGroup) {
      return this.group?.avatarTitle;
    }

    if (this.senderUser) {
      return this.senderUser.avatarTitle;
    }

    return '';
  }

  @computed
  get avatarColorIndex() {
    if (this.forward) {
      if (this.isForwardToSavedMessages) {
        if (this.forward.isFromChannel) {
          return this.forward.groupFrom?.avatarColorIndex || '0';
        }
        return Entities.usersStore.getUserById(this.forward.senderId)?.avatarColorIndex || '0';
      }
    }

    if (this.isPublicGroup) {
      return this.group.avatarColorIndex;
    }

    return this.senderUser.avatarColorIndex;
  }

  @computed
  get lastMessageSenderName() {
    if (this.group.isChannel) {
      return '';
    }

    if (this.isForwardToSavedMessages) {
      return this.forward.avatarTitle;
    }

    if (this.senderUser) {
      return this.senderUser.avatarTitle;
    }

    return '';
  }

  @computed
  get isArticleDraft() {
    return this.group.draftsStore.data.has(this.id);
  }

  @action
  setId(id) {
    this.id = id;
  }

  @action
  setOptimisticReactions = (reactions) => {
    this.optimisticReactions = reactions;
  };

  @action
  setDeepLink = (deepLink) => {
    this.deepLink = deepLink;
  };

  @action
  translate = async () => {
    await Tasks.messaging.translateMessage({
      groupId: this.groupId,
      messageId: this.id,
    });
  };

  @action
  toggleTranslate = () => {
    this.isShowTranslate = !this.isShowTranslate;
  };

  @action
  setViews = (views) => {
    this.views = views;
  };
}
